import * as qs from 'qs';
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useAddJobToNotificationQueue } from '../../../components/JobNotificationHub';
import { useReportOptionsParams } from '../../../components/reportsv3/hooks/useReportOptionsParams';
import { isReleaseGroupScope, type ReportsV3Params, type ReportV3Format } from '../../../components/reportsv3/types';
import { useParams } from '../../../contexts/RouterContext';
import useToastedMutation from '../../../ReactQuery/useToastedMutation';
import { get } from '../../../redux/api';
import ProjectGroupsModule from '../../../redux/project_grouping';

/*
 * The publish endpoint enqueues this Faktory job. Unlike most task endpoints it
 * returns only the task token (no job name), so we label the job ourselves so
 * the JobNotificationHub can map it to the right completion toast (see the
 * matching key in the hub's consts).
 */
const SBOM_PUBLISH_JOB_NAME = 'GenerateReleaseGroupReport';

/**
 * Hook that returns a mutation to publish an SBOM report to the organization's
 * SBOM portal. Publishing is only supported at the release group level, so the
 * mutation builds no URL (and is never invoked) outside of release group scope.
 *
 * On success the release is refetched so it immediately shows as `pending`, and
 * the returned task is registered with the JobNotificationHub so a toast fires
 * once the background publish job finishes. The job's `onFinished` callback
 * refetches the release again so the status flips to "Last published" without a
 * page reload.
 *
 * @param format The report format to publish.
 */
export function usePublishReport(format: ReportV3Format) {
  const params = useParams<ReportsV3Params>();
  const isReleaseGroup = isReleaseGroupScope(params);
  const selectedReleaseId = useSelector(ProjectGroupsModule.selectors.getSelectedReleaseId);
  const reportOptionsParams = useReportOptionsParams();
  const dispatch = useDispatch();
  const addJob = useAddJobToNotificationQueue();

  const releaseGroupIds = useMemo(() => {
    if (!isReleaseGroup) {
      return null;
    }
    const { projectGroupId, releaseId } = params;
    const resolvedReleaseId = releaseId ?? selectedReleaseId;
    if (resolvedReleaseId == null) {
      return null;
    }
    return {
      projectGroupId: Number(projectGroupId),
      releaseId: Number(resolvedReleaseId),
    };
  }, [isReleaseGroup, params, selectedReleaseId]);

  return useToastedMutation({
    mutationFn: () => {
      if (!releaseGroupIds) {
        return Promise.reject(new Error('SBOM publishing requires a loaded release group target.'));
      }
      return get<{ taskId: string }>(
        `/api/v2/project_group/${releaseGroupIds.projectGroupId}/release/${releaseGroupIds.releaseId}/attribution/${format}`,
        false,
        {
          ...qs.parse(reportOptionsParams),
          isPublishing: true,
          format,
        },
      );
    },
    onSuccess: (response) => {
      const refetchRelease = () =>
        dispatch(ProjectGroupsModule.actions.fetchRelease(releaseGroupIds!.projectGroupId, releaseGroupIds!.releaseId));
      // Refetch now so the status shows `pending`...
      refetchRelease();
      const taskId = response?.data?.taskId;
      if (taskId) {
        // ...and again once the background job finishes, so it flips to published.
        addJob(taskId, SBOM_PUBLISH_JOB_NAME, { onFinished: refetchRelease });
      }
    },
    successToastMessage: 'Publishing your SBOM to the portal. This may take a few moments.',
    errorToastMessage: 'Failed to publish SBOM to the portal',
  });
}
